import React from 'react'
import { Link } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { css } from 'linaria'
import { useLocation } from '@reach/router'
import SmoothScroll from '~/components/Common/SmoothScroll'
import { STYLE } from '~/utils/constant'
import { rem } from '~/utils/helper'

type NavigationItem = {
  label: string[]
  path: string
  hash?: string
  isBlank?: boolean
}

const features: NavigationItem[] = [
  {
    label: ['3つのメリット'],
    path: '/',
    hash: 'advantage',
  },
  {
    label: ['選ばれる理由'],
    path: '/',
    hash: 'reason',
  },
  {
    label: ['機能'],
    path: '/',
    hash: 'function',
  },
  {
    label: ['サポート体制'],
    path: '/',
    hash: 'support',
  },
  {
    label: ['料金プラン'],
    path: '/',
    hash: 'price',
  },
  {
    label: ['導入事例'],
    path: '/',
    hash: 'case',
  },
  {
    label: ['トピックス'],
    path: '/',
    hash: 'topics',
  },
  {
    label: ['FAQ'],
    path: '/',
    hash: 'faq',
  },
]

const options: NavigationItem[] = [
  {
    label: ['バックグラウンド', 'チェック'],
    path: '/backgroundcheck',
    hash: '',
  },
]

const infomations: NavigationItem[] = [
  {
    label: ['資料一覧'],
    path: '/resources',
    hash: '',
  },
  {
    label: ['イベント'],
    path: '/event',
    hash: '',
  },
  {
    label: ['お役立ち情報'],
    path: '/news',
    hash: '',
  },
  {
    label: ['リファレンス', 'チェックとは'],
    path: '/news/b5et32a5cvfs',
    hash: '',
  },
  {
    label: ['バックグラウンド', 'チェックとは'],
    path: '/news/ap42jcqzt',
    hash: '',
  },
]

const others: NavigationItem[] = [
  {
    label: ['運営会社'],
    path: 'https://corp.en-japan.com/',
    hash: '',
    isBlank: true,
  },
  {
    label: ['プライバシーポリシー'],
    path: 'https://corp.en-japan.com/privacy/',
    hash: '',
    isBlank: true,
  },
  {
    label: ['利用規約'],
    path: '/terms',
    hash: '',
    isBlank: false,
  },
]

const footerStyle = css`
  order: 3;
  padding-bottom: 72px;
  background: var(--color-background-bluegray800);

  @media screen and (${STYLE.MEDIA.TABLET}) {
    order: 0;
    margin-top: 144px;
  }

  & > .body {
    position: relative;
    max-width: var(--width-max-content);
    margin-right: auto;
    margin-left: auto;
    padding-top: 40px;
    padding-right: 32px;
    padding-left: 32px;

    @media screen and (${STYLE.MEDIA.PC}) {
      padding-top: 64px;
    }
  }

  & > .body > .content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  & > .body > .content > .logowrapper {
    margin-top: 8px;
  }

  & > .body > .content > .logowrapper > .button {
    display: block;
    backface-visibility: hidden;
  }

  & > .body > .content > .logowrapper > .button > .logo {
    display: block;
    width: 150px;
    height: auto;

    @media screen and (${STYLE.MEDIA.TABLET}) {
      width: 180px;
    }
  }

  & > .body > .content > .navigation {
    margin-top: 32px;
  }

  & > .body > .content > .navigation > .body {
    display: grid;
    grid-column-gap: 24px;
    grid-row-gap: 32px;
    grid-template-columns: repeat(2, calc(50% - 12px));
    grid-template-rows: repeat(2, auto);

    @media screen and (${STYLE.MEDIA.TABLET}) {
      grid-column-gap: 48px;
      grid-template-columns: repeat(2, calc(50% - 24px));
      width: 640px;
    }

    @media screen and (${STYLE.MEDIA.PC}) {
      grid-column-gap: 96px;
      grid-template-columns: repeat(2, calc(50% - 48px));
      width: 720px;
    }
  }

  & > .body > .content > .navigation > .others {
    margin-top: 48px;
    border-top: 1px solid var(--color-border-bright);
  }

  & > .body > .content > .navigation > .others > .items {
    display: flex;
    flex-direction: column;
    margin-top: 48px;
    row-gap: 16px;

    @media screen and (${STYLE.MEDIA.TABLET}) {
      flex-direction: row;
      column-gap: 32px;
    }
  }

  & > .body > .content > .navigation > .others > .items > .item > .button {
    color: var(--color-text-bright);
    font-size: ${rem(14)};
    text-decoration: none;
  }

  & > .body > .copyright {
    margin-top: 40px;

    @media screen and (${STYLE.MEDIA.TABLET}) {
      position: absolute;
      right: 32px;
      bottom: 4px;
      margin-top: 0;
    }
  }

  & > .body > .copyright > .text {
    display: block;
    color: var(--color-text-bright);
    font-size: ${rem(12)};
    font-weight: 400;
  }
`

const navigationItemStyle = css`
  grid-area: 1 / 1 / 3 / 2;

  @media screen and (${STYLE.MEDIA.TABLET}) {
    grid-area: 1 / 1 / 2 / 2;
  }

  @media screen and (${STYLE.MEDIA.PC}) {
    flex-basis: auto;
  }

  & > .title {
    color: var(--color-text-bluegray100);
    font-size: ${rem(12)};
  }

  & > .body {
    margin-top: 24px;
  }

  & > .body > .items {
    display: flex;
    flex-direction: column;
    row-gap: 16px;
  }

  & > .body > .items > .item {
    position: relative;
    padding-left: 0.9em;

    &::before {
      content: '';
      position: absolute;
      top: 0.8em;
      left: 0;
      width: 0.5em;
      height: 1px;
      background: var(--color-background-bright);
    }
  }

  & > .body > .items > .item > .button {
    color: var(--color-text-bright);
    font-weight: 700;
    line-height: 1.3;
    text-decoration: none;
  }

  & > .body > .items > .item > .button > .text {
    display: inline-block;
  }
`

const navigationFeatureItemStyle = css`
  & > .body {
    @media screen and (${STYLE.MEDIA.TABLET}) {
      width: 300px;
    }
  }

  & > .body > .items {
    @media screen and (${STYLE.MEDIA.TABLET}) {
      flex-flow: row wrap;
    }
  }

  & > .body > .items > .item {
    @media screen and (${STYLE.MEDIA.TABLET}) {
      flex-basis: 50%;
    }
  }
`

const navigationOptionItemStyle = css`
  grid-area: 1 / 2 / 2 / 3;

  @media screen and (${STYLE.MEDIA.TABLET}) {
    grid-area: 2 / 1 / 3 / 2;
  }
`

const navigationInfoItemStyle = css`
  grid-area: 2 / 2 / 3 / 3;

  @media screen and (${STYLE.MEDIA.TABLET}) {
    grid-area: 1 / 2 / 3 / 3;
  }
`

const isTopStyle = css`
  margin-top: 0;
`

const FooterComponent = () => {
  const { pathname } = useLocation()
  const logo = (
    <StaticImage
      className="logo"
      src="../../images/logo-white.svg"
      placeholder="none"
      alt="リファレンスチェックサービス ASHIATO"
    />
  )

  const links = (items: NavigationItem[]) => {
    return items.map((item: NavigationItem) => {
      const href = item.hash ? `${item.path}#${item.hash}` : item.path
      const texts =
        item.label.length === 1
          ? item.label[0]
          : item.label.map((text, index) => {
              return (
                <span className="text" key={index}>
                  {text}
                </span>
              )
            })
      const link = () => {
        if (item.isBlank) {
          return (
            <a href={href} className="button" target="_blank" rel="noreferrer">
              {texts}
            </a>
          )
        } else if (pathname === item.path && item.hash) {
          // Note: ページ内スクロール
          return (
            <SmoothScroll className="button" to={item.hash} href={href}>
              {texts}
            </SmoothScroll>
          )
        } else {
          return (
            <Link to={href} className="button">
              {texts}
            </Link>
          )
        }
      }
      return (
        <li className="item" key={item.label[0]}>
          {link()}
        </li>
      )
    })
  }

  return (
    <footer
      className={pathname === '/' || pathname === '/backgroundcheck' ? `${footerStyle} ${isTopStyle}` : footerStyle}
    >
      <div className="body">
        <div className="content">
          <div className="logowrapper">
            {pathname === '/' ? (
              <SmoothScroll className="button" to="top" href="/">
                {logo}
              </SmoothScroll>
            ) : (
              <Link to="/" className="button">
                {logo}
              </Link>
            )}
          </div>
          <nav className="navigation">
            <div className="body">
              <dl className={`${navigationItemStyle} ${navigationFeatureItemStyle}`}>
                <dt className="title">ASHIATOの特徴</dt>
                <dd className="body">
                  <ul className="items">{links(features)}</ul>
                </dd>
              </dl>
              <dl className={`${navigationItemStyle} ${navigationOptionItemStyle}`}>
                <dt className="title">オプション機能</dt>
                <dd className="body">
                  <ul className="items">{links(options)}</ul>
                </dd>
              </dl>
              <dl className={`${navigationItemStyle} ${navigationInfoItemStyle}`}>
                <dt className="title">お役立ち情報</dt>
                <dd className="body">
                  <ul className="items">{links(infomations)}</ul>
                </dd>
              </dl>
            </div>
            <div className="others">
              <ul className="items">{links(others)}</ul>
            </div>
          </nav>
        </div>
        <div className="copyright">
          <small className="text">&copy;2020-{String(new Date().getFullYear())} en Japan Inc.</small>
        </div>
      </div>
    </footer>
  )
}

export default FooterComponent
